import { useQuery } from '@tanstack/react-query';
import { Gamepad2 } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { allocationsApi } from '@/features/stock/api';
import { xurveyApi } from '@/lib/api/xurvey';
import type { XkStation } from '@/lib/api/types';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import { slotCount } from '@/features/public-survey/games/fillSlots';

/**
 * «Qué juego corre aquí», de un vistazo.
 *
 * Un chip en la tarjeta del evento o de la estación que contesta las dos
 * preguntas que uno se hace antes de abrir nada: qué juego se juega y cuántas
 * casillas tiene.
 *
 * ## De dónde sale el juego
 *
 * Del **despliegue**, que es quien lo decide. La encuesta dice «aquí va un
 * juego»; cuál se juega lo elige cada puesta en marcha, y por eso la misma
 * plantilla lleva ruleta en una activación y rasca-rasca en la siguiente.
 *
 * Esto leía antes `bs_event.gameMechanic`, del modelo anterior. Aquello dejó de
 * escribirse cuando el juego pasó al despliegue —el backend sólo lo mantiene
 * fijado a `'wheel'` por compatibilidad—, así que el chip llevaba tiempo
 * anunciando una ruleta encima de eventos que jugaban a otra cosa. Un chip que
 * miente es peor que no tenerlo: se mira precisamente para no abrir la ficha.
 *
 * Sin `gameCode` propio el despliegue se queda con el que traiga la encuesta.
 * Ahí se dice eso mismo en vez de inventar un nombre: el chip no tiene el árbol
 * de la encuesta delante y afirmar cuál es sería volver a adivinar.
 *
 * ## Cuándo no se pinta
 *
 * Cuando el evento no tiene ninguna encuesta desplegada. Un chip vacío es ruido
 * en una tarjeta que ya dice que el evento está sin configurar.
 *
 * Resuelve las casillas por su cuenta en vez de recibirlas por prop: la fila del
 * evento sólo carga las asignaciones al desplegarse, así que un número recibido
 * de fuera diría «0 casillas» justo cuando la tarjeta está plegada y el vistazo
 * es lo único que hay. Las claves de consulta son las mismas que usan la fila y
 * el Design Journey, así que al abrirlos comparten caché en lugar de pedir dos
 * veces.
 */
export function GameGlanceChip({
  eventId,
  stationId,
}: {
  eventId: number;
  /** Con esto, cuenta sólo las porciones de esta estación y no las del evento. */
  stationId?: number;
}) {
  const t = useT();

  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byEvent(eventId),
    queryFn: () => eventsApi.listStations({ eventId }),
    enabled: eventId > 0,
  });
  const stations = (stationsQ.data?.items ?? []) as XkStation[];

  // `surveysId` es la columna que Xotski mantiene al desplegar: qué encuesta
  // corre esta estación. Se pregunta a las estaciones y no a una tabla de
  // configuración porque es el mismo dato que decide qué se sirve en el QR.
  const surveyId = stations.find((s) => s.surveysId)?.surveysId ?? null;

  const deploymentsQ = useQuery({
    queryKey: ['xurvey', 'deployments', surveyId],
    queryFn: () => xurveyApi.listDeployments(surveyId!),
    enabled: surveyId != null,
  });

  const allocQ = useQuery({
    queryKey: queryKeys.stockAllocations.byEvent(eventId),
    queryFn: () => allocationsApi.list({ eventId }),
    enabled: eventId > 0,
  });

  // Los despliegues de una encuesta son de toda la empresa; aquí interesan los
  // que caen en las estaciones de ESTE evento (o en la estación pedida).
  const propias = stations.filter((s) => stationId == null || s.id === stationId);
  const mios = (deploymentsQ.data ?? []).filter((d) =>
    propias.some((s) => String(s.id) === String(d.entityId)),
  );

  if (surveyId == null || mios.length === 0) return null;

  const code = mios.find((d) => d.gameCode)?.gameCode ?? null;

  const rows = (allocQ.data?.items ?? []).filter(
    (a) => a.status === 'Enable' && (stationId == null || a.stationId === stationId),
  );
  // Lo que ve el invitado, no el número de tipos de premio: con menos de seis
  // tipos se repiten hasta llenar el juego.
  const slots = slotCount(rows.length);

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[10px] font-bold text-[var(--color-fg-muted)]"
      title={t('planner.gameGlance.title')}
    >
      <Gamepad2 className="h-3 w-3 text-[var(--color-accent-ink)]" />
      {code ? gameLabel(code, t) : t('planner.gameGlance.game.fromSurvey')}
      <span className="text-[var(--color-fg-subtle)]">·</span>
      <span className="tabular-nums">
        {slots} {t(slots === 1 ? 'planner.gameGlance.slotOne' : 'planner.gameGlance.slotMany')}
      </span>
    </span>
  );
}

/**
 * Los códigos del catálogo vienen de `g_game` y no son texto de cara al
 * usuario. Se traducen los conocidos; lo que no esté cae a su propio código en
 * vez de esconderse, para que un juego añadido por una migración siga diciendo
 * algo cierto.
 */
function gameLabel(mechanic: string, t: (k: string) => string): string {
  const KEY: Record<string, string> = {
    wheel: 'planner.gameGlance.game.wheel',
    scratch: 'planner.gameGlance.game.scratch',
    xinkit: 'planner.gameGlance.game.xinkit',
    // Código anterior de Xinkit, previo a la migración 092.
    plinko: 'planner.gameGlance.game.xinkit',
    pirinola: 'planner.gameGlance.game.pirinola',
    slots: 'planner.gameGlance.game.slots',
    'random-draw': 'planner.gameGlance.game.randomDraw',
    soccer: 'planner.gameGlance.game.soccer',
  };
  const key = KEY[mechanic];
  return key ? t(key) : mechanic;
}
